import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Scrollbars from "react-custom-scrollbars-2";
import { SidebarData } from "../../data/json/sidebarData";
import ImageWithBasePath from "../imageWithBasePath";
import { useSelector, useDispatch } from "react-redux";
import { all_routes } from "../../../feature-module/router/all_routes";
import { checkModulePermission } from "../../utils/permissions";
import axiosInstance, { FEATURE_DISABLED_EVENT } from "../../data/interface/axiosInstance";
import { getBaseUrl } from "../../data/interface/getBaseUrl";
import { setPermissionKeys, setEnabledModules } from "../../data/redux/permissionsSlice";
import "./unauthorized.css";

// =============================================================================
// Label → i18n key mapping.
//
// Kept OUT of sidebarData.tsx so that data file stays untouched. When we add
// a new menu item, either add the key here or leave it out — the helper falls
// back to the raw English label if no key is found.
// =============================================================================
const SECTION_LABEL_TO_KEY: Record<string, string> = {
  "MAIN MENU":              "nav:sections.mainMenu",
  "PEOPLE & HR":            "nav:sections.peopleHr",
  "OPERATIONS":             "nav:sections.operations",
  "TRAINING & COMPLIANCE":  "nav:sections.trainingCompliance",
  "AUDIT":                  "nav:sections.audit",
  "CUSTOMER MANAGEMENT":    "nav:sections.customerManagement",
  "REPORTS":                "nav:sections.reports",
  "NOTIFICATIONS":          "nav:sections.notifications",
  "SETTINGS":               "nav:sections.settings",
};

const ITEM_LABEL_TO_KEY: Record<string, string> = {
  "Dashboard":                "nav:items.dashboard",
  "Employees":                "nav:items.employees",
  "Employee Types":           "nav:items.employeeTypes",
  "Contract Type":            "nav:items.contractType",
  "Departments":              "nav:items.departments",
  "Attendance":               "nav:items.attendance",
  "Holiday Request":          "nav:items.holidayRequest",
  "Areas":                    "nav:items.areas",
  "Induction Templates":      "nav:items.inductionTemplates",
  "Volunteer":                "nav:items.volunteer",
  "Clothing Item":            "nav:items.clothingItem",
  "Clothing Request":         "nav:items.clothingRequest",
  "Clothing Returns":         "nav:items.clothingReturns",
  "Customers":                "nav:items.customers",
  "Customer Permissions":     "nav:items.customerPermissions",
  "Customer Request Report":  "nav:items.customerRequestReport",
  "Farms":                    "nav:items.farms",
  "Teams":                    "nav:items.teams",
  "Vehicles & Assets":        "nav:items.vehiclesAssets",
  "Van Photo":                "nav:items.vanPhoto",
  "Tracking":                 "nav:items.tracking",
  "Sanitization":             "nav:items.sanitization",
  "Audit Type":               "nav:items.auditType",
  "Audit Scheduling":         "nav:items.auditScheduling",
  "Audit Report":             "nav:items.auditReport",
  "Reports Submission":       "nav:items.reportsSubmission",
  "NCR Report Submission":    "nav:items.ncrReportSubmission",
  "NCR Template":             "nav:items.ncrTemplate",
  "Incident Report":          "nav:items.incidentReport",
  "Auditing Clock In/Out":    "nav:items.auditingClockInOut",
  "Training Dashboard":       "nav:items.trainingDashboard",
  "Trainings":                "nav:items.trainings",
  "Training Assignments":     "nav:items.trainingAssignments",
  "My Trainings":             "nav:items.myTrainings",
  "SOP":                      "nav:items.sop",
  "Certifications":           "nav:items.certifications",
  "Important Documents":      "nav:items.importantDocuments",
  "Training Record Requests": "nav:items.trainingRecordRequests",
  "Certificate Types":        "nav:items.certificateTypes",
  "Submit Record Request":    "nav:items.submitRecordRequest",
  "Assign Training (Area Manager)": "nav:items.areaManagerAssignTraining",
  "Reports":                  "nav:items.reports",
  "Notification Types":       "nav:items.notificationTypes",
  "Notification Template":    "nav:items.notificationTemplate",
  "Roles & Permission":       "nav:items.rolesPermission",
  "Check In/Out Settings":    "nav:items.checkInOutSettings",
};

interface MenuItem {
  label: string;
  name: string;
  icon: string;
  showSubRoute: boolean;
  link: string;
  submenu: boolean;
  submenuItems?: MenuItem[];
}

// 🔹 Helper: determine if a menu item is active
function isMenuActive(currentPath: string, menuItem: any, allMenuItems: any[] = []) {
  if (menuItem.submenuItems && menuItem.submenuItems.length > 0) {
    for (const sub of menuItem.submenuItems) {
      if (isMenuActive(currentPath, sub, menuItem.submenuItems)) {
        return false;
      }
    }
  }
  if (currentPath === menuItem.link) return true;
  if (
    menuItem.link &&
    currentPath.startsWith(menuItem.link + "/") &&
    !allMenuItems.some(
      (item: any) => item.link !== menuItem.link && currentPath.startsWith(item.link)
    )
  ) {
    return true;
  }
  return false;
}
const Sidebar = () => {
  const { t } = useTranslation();
  const Location = useLocation();
  const user = useSelector((state: any) => state.auth?.user);
  // Permissions V2 — re-filter the menu whenever the granted key set changes.
  const permissionKeys = useSelector((state: any) => state.permissions?.permissionKeys);
  const dispatch = useDispatch();
  const lastRefreshRef = useRef(0);

  // Keep the granted key set fresh WITHOUT depending on a live SignalR socket:
  // re-pull permissionKeys on navigation and on tab-focus (throttled to 10s). This
  // makes the menu reflect permission changes within a click even if the real-time
  // push didn't arrive (socket down, reconnecting, etc.).
  const refreshPermissions = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshRef.current < 10000) return;
    lastRefreshRef.current = now;
    try {
      const res = await axiosInstance.post(`${getBaseUrl(true)}/Account/RefreshPermissions`);
      const keys = res?.data?.dataModel?.permissionKeys;
      if (Array.isArray(keys)) dispatch(setPermissionKeys(keys));
      dispatch(setEnabledModules(res?.data?.dataModel?.enabledModules));
    } catch { /* silent — keep last-known keys */ }
  }, [dispatch]);

  useEffect(() => {
    const onFocus = () => refreshPermissions();
    const onVisible = () => { if (document.visibilityState === "visible") refreshPermissions(); };
    // The API refused a module the company has switched off: refresh now, past the
    // throttle (axiosInstance already throttles this event).
    const onFeatureDisabled = () => { lastRefreshRef.current = 0; refreshPermissions(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(FEATURE_DISABLED_EVENT, onFeatureDisabled);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(FEATURE_DISABLED_EVENT, onFeatureDisabled);
    };
  }, [refreshPermissions]);

  // Re-sync on route change too (covers same-tab navigation after a grant change).
  useEffect(() => { refreshPermissions(); }, [Location.pathname, refreshPermissions]);
  // Translate a sidebar label using the lookup table; fall back to the raw
  // English label if no i18n key is registered for it.
  const trSection = (label: string) => {
    const key = SECTION_LABEL_TO_KEY[label];
    return key ? t(key, label) : label;
  };
  const trItem = (label: string) => {
    const key = ITEM_LABEL_TO_KEY[label];
    return key ? t(key, label) : label;
  };

  const [subOpen, setSubopen] = useState("");
  const [subsidebar, setSubsidebar] = useState("");
  const [filteredSidebarData, setFilteredSidebarData] = useState(SidebarData);

  // Tenancy §7.1: items gated by isPlatformAdmin instead of a Permissions V2
  // key — there is no "platform admin" permission key, it's a separate claim
  // (see AuthIdentityCustom.IsPlatformAdmin / TENANCY-DESIGN.md §3.2).
  const PLATFORM_ADMIN_ONLY_ITEMS = new Set(["PlatformAdminCompanies", "PlatformAdminSharing", "PlatformAdminReplication", "PlatformAdminCopying"]);
  const isPlatformAdmin = useSelector((state: any) => state.auth?.user?.isPlatformAdmin);

  // 🔹 Permissions V2 — menu visibility is decided from the granted key set.
  // checkModulePermission maps the sidebar's module name to its key prefix and
  // returns true when the role holds ANY key under it.
  const shouldShowMenuItem = (item: MenuItem) => {
    // Tenancy §3.2 / §7.1: a platform (super) admin belongs to no tenant and
    // has no tenant data — they see ONLY the platform-admin surface
    // (Companies + Data Sharing), nothing else, regardless of permission keys.
    if (isPlatformAdmin) return PLATFORM_ADMIN_ONLY_ITEMS.has(item.name);
    if (PLATFORM_ADMIN_ONLY_ITEMS.has(item.name)) return false;
    return checkModulePermission(item.name, "canAccess");
  };
  // 🔹 Filter sidebar based on user permissions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const filtered = SidebarData.map(mainLabel => ({
      ...mainLabel,
      submenuItems: mainLabel.submenuItems?.filter((item: MenuItem) => {
        const hasPermission = shouldShowMenuItem(item);
        if (!hasPermission) return false;

        if (item.submenuItems) {
          const filteredSubItems = item.submenuItems.filter((subItem: MenuItem) => {
            const subItemHasPermission = shouldShowMenuItem(subItem);
            if (!subItemHasPermission) return false;

            if (subItem.submenuItems) {
              const filteredThirdLevel = subItem.submenuItems.filter((thirdLevelItem: MenuItem) =>
                shouldShowMenuItem(thirdLevelItem)
              );
              return filteredThirdLevel.length > 0;
            }
            return true;
          });
          return filteredSubItems.length > 0;
        }

        return true;
      }),
    })).filter(mainLabel => mainLabel.submenuItems?.length > 0);

    setFilteredSidebarData(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionKeys, isPlatformAdmin]);

  const toggleSidebar = (title: string) => {
    localStorage.setItem("menuOpened", title);
    setSubopen(subOpen === title ? "" : title);
  };

  const toggleSubsidebar = (subitem: string) => {
    setSubsidebar(subsidebar === subitem ? "" : subitem);
  };

  useEffect(() => {
    setSubopen(localStorage.getItem("menuOpened") || "");
    const submenus = document.querySelectorAll(".submenu");
    submenus.forEach(submenu => {
      const listItems = submenu.querySelectorAll("li");
      submenu.classList.remove("active");
      listItems.forEach(item => {
        if (item.classList.contains("active")) {
          submenu.classList.add("active");
          return;
        }
      });
    });
  }, [Location.pathname]);

  // Permissions V2 — the key set hydrates synchronously from localStorage, but guard
  // the render until it's a resolved array (never block on an empty array — a role with
  // no grants legitimately sees an empty menu).
  if (!Array.isArray(permissionKeys)) {
    return (
      <div
        className="sidebar-loading d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar" id="sidebar">
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div className="sidebar-header p-3 pb-0 pt-2">
            <div className="d-flex align-items-center justify-content-between menu-item mb-3">
              <Link to={all_routes.calendar} className="btn btn-icon border btn-menubar">
                <i className="ti ti-layout-grid-remove" />
              </Link>
              <Link to={all_routes.chat} className="btn btn-icon border btn-menubar position-relative">
                <i className="ti ti-brand-hipchat" />
              </Link>
              <Link to={all_routes.activities} className="btn btn-icon border btn-menubar position-relative">
                <i className="ti ti-bell" />
                <span className="notification-status-dot" />
              </Link>
              <Link to={all_routes.email} className="btn btn-icon border btn-menubar">
                <i className="ti ti-message" />
              </Link>
            </div>
          </div>

          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              <li className="clinicdropdown theme">
                <Link to="/profile">
                  <ImageWithBasePath
                    src={user?.profileImage || "assets/img/profiles/avatar-14.jpg"}
                    className="img-fluid"
                    alt="Profile"
                  />
                  <div className="sidebar-user-profile">
                    <h5 className="user-name">{user?.name || "N/A"}</h5>
                    <h6 className="user-email">{user?.email || "N/A"}</h6>
                  </div>
                </Link>
              </li>
            </ul>

            <ul>
              {filteredSidebarData.map((mainLabel, index) => (
                <li className="clinicdropdown" key={index}>
                  <h6 className="submenu-hdr">{trSection(mainLabel.label)}</h6>
                  <ul>
                    {mainLabel.submenuItems?.map((title: any) => {
                      const active = isMenuActive(Location.pathname, title, mainLabel.submenuItems);
                      return (
                        <li className="submenu" key={title.label}>
                          <Link
                            to={title.submenu ? "#" : title.link}
                            onClick={e => {
                              if (title.submenu) {
                                e.preventDefault();
                                toggleSidebar(title.label);
                              }
                            }}
                            className={`${subOpen === title.label ? "subdrop" : ""} ${active ? "active" : ""
                              }`}
                          >
                            <i className={title.icon}></i>
                            <span>{trItem(title.label)}</span>
                            <span className={title.submenu ? "menu-arrow" : ""} />
                          </Link>

                          <ul style={{ display: subOpen === title.label ? "block" : "none" }}>
                            {title.submenuItems?.map((item: any) => {
                              const subActive = isMenuActive(Location.pathname, item, title.submenuItems);
                              return (
                                <li className="submenu submenu-two" key={item.label}>
                                  <Link
                                    to={item.link}
                                    className={subActive ? "active subdrop" : ""}
                                    onClick={() => toggleSubsidebar(item.label)}
                                  >
                                    {trItem(item.label)}
                                    <span className={item.submenu ? "menu-arrow" : ""} />
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default Sidebar;
